// AuthProvider و useAuth (AP-008): ورود OIDC، Callback، خروج و ورود مجدد پس از قفل صفحه.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthClient, AuthUser, InitResult } from './authClient';

export type AuthStatus =
  | 'initializing'
  | 'authenticated'
  /** بدون نشست روی مسیر عمومی (پذیرش دعوت، بازیابی رمز — AP-IAM-02). */
  | 'anonymous'
  | 'redirecting'
  | 'popup-callback'
  | 'ended'
  | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: unknown;
  getAccessToken: AuthClient['getAccessToken'];
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** تغییر رمز با Required Action کیکلاک (UPDATE_PASSWORD) و بازگشت به returnTo (SCR-STAFF-002). */
  changePassword: (returnTo?: string) => Promise<void>;
  /** پیکربندی TOTP (Required Action کیکلاک — AP-IAM-03). */
  configureTotp: (returnTo?: string) => Promise<void>;
  /** ورود مجدد سریع: true اگر همان کاربر دوباره احراز شد. کاربر متفاوت ← خروج کامل (امنیت قفل صفحه). */
  reauthenticate: () => Promise<boolean>;
  pauseRenew: () => void;
  resumeRenew: () => void;
  /**
   * ثبت کار پیش از خروج (مثلاً همگام‌سازی و پاک‌سازی گاوصندوق آفلاین — ARD-offline §۲، AP-OFF-01)؛ خروج منتظر همه می‌ماند (حداکثر ۵ ثانیه).
   * تابع برگشتی ثبت را برمی‌دارد.
   */
  onBeforeLogout: (handler: () => Promise<void> | void) => () => void;
}

/** بیشینه انتظار خروج برای کارهای پیش از خروج. */
const BEFORE_LOGOUT_TIMEOUT_MS = 5_000;

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  client: AuthClient;
  children: ReactNode;
  /** نمایش در حین آماده‌سازی یا انتقال به Keycloak. */
  fallback?: ReactNode;
  /** نمایش وقتی نشست Keycloak پایان یافته (تمدید ناموفق). */
  renderEnded?: (login: () => void) => ReactNode;
  renderError?: (error: unknown, retry: () => void) => ReactNode;
  /** بدون نشست، خودکار به Keycloak برود (پیش‌فرض true). */
  autoLogin?: boolean;
  /**
   * مسیرهای عمومی (بدون ورود): نشست ناموجود ← وضعیت anonymous و نمایش children به‌جای انتقال به Keycloak
   * (صفحات پذیرش دعوت و بازیابی رمز — AP-IAM-02، C-AGENT-220).
   */
  allowAnonymous?: (pathname: string) => boolean;
}

// init یک‌بار به ازای هر AuthClient (StrictMode اثرها را دوبار اجرا می‌کند و Callback یک‌بارمصرف است).
const initCache = new WeakMap<AuthClient, Promise<InitResult>>();

export function AuthProvider({
  client,
  children,
  fallback = null,
  renderEnded,
  renderError,
  autoLogin = true,
  allowAnonymous,
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const userRef = useRef<AuthUser | null>(null);
  const beforeLogout = useRef(new Set<() => Promise<void> | void>());

  useEffect(() => {
    let cancelled = false;
    let pending = initCache.get(client);
    if (!pending) {
      pending = client.init();
      initCache.set(client, pending);
    }
    pending
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        if (result.kind === 'popup-callback') {
          setStatus('popup-callback');
        } else if (result.kind === 'authenticated') {
          userRef.current = result.user;
          setUser(result.user);
          setStatus('authenticated');
        } else if (allowAnonymous?.(window.location.pathname)) {
          setStatus('anonymous');
        } else if (autoLogin) {
          setStatus('redirecting');
          await client.login();
        } else {
          setStatus('ended');
        }
      })
      .catch((reason: unknown) => {
        initCache.delete(client);
        if (!cancelled) {
          setError(reason);
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
    // allowAnonymous فقط در نخستین آماده‌سازی خوانده می‌شود.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, autoLogin, attempt]);

  useEffect(
    () =>
      client.onSessionEnded(() => setStatus((current) => (current === 'authenticated' ? 'ended' : current))),
    [client],
  );

  const login = useCallback(
    async (returnTo?: string) => {
      setStatus('redirecting');
      await client.login(returnTo);
    },
    [client],
  );

  const logout = useCallback(async () => {
    const handlers = [...beforeLogout.current];
    if (handlers.length > 0) {
      // شکست یا کندی کارهای پیش از خروج خروج را متوقف نمی‌کند.
      await Promise.race([
        Promise.allSettled(handlers.map(async (handler) => handler())),
        new Promise((resolve) => setTimeout(resolve, BEFORE_LOGOUT_TIMEOUT_MS)),
      ]);
    }
    setStatus('redirecting');
    userRef.current = null;
    await client.logout();
  }, [client]);

  const onBeforeLogout = useCallback((handler: () => Promise<void> | void) => {
    beforeLogout.current.add(handler);
    return () => {
      beforeLogout.current.delete(handler);
    };
  }, []);

  const changePassword = useCallback(
    async (returnTo?: string) => {
      setStatus('redirecting');
      await client.changePassword(returnTo);
    },
    [client],
  );

  const configureTotp = useCallback(
    async (returnTo?: string) => {
      setStatus('redirecting');
      await client.configureTotp(returnTo);
    },
    [client],
  );

  const reauthenticate = useCallback(async () => {
    const previous = userRef.current;
    const next = await client.reauthenticate(previous?.username);
    if (!next) {
      return false;
    }
    if (!previous || next.subject !== previous.subject) {
      await logout();
      return false;
    }
    userRef.current = next;
    setUser(next);
    setStatus('authenticated');
    return true;
  }, [client, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      getAccessToken: (options) => client.getAccessToken(options),
      login,
      logout,
      changePassword,
      configureTotp,
      reauthenticate,
      pauseRenew: () => client.pauseRenew(),
      resumeRenew: () => client.resumeRenew(),
      onBeforeLogout,
    }),
    [status, user, error, client, login, logout, changePassword, configureTotp, reauthenticate, onBeforeLogout],
  );

  let content: ReactNode;
  if (status === 'authenticated' || status === 'anonymous') {
    content = children;
  } else if (status === 'ended' && renderEnded) {
    content = renderEnded(() => void login());
  } else if (status === 'error' && renderError) {
    content = renderError(error, () => {
      setStatus('initializing');
      setAttempt((n) => n + 1);
    });
  } else if (status === 'popup-callback') {
    content = null;
  } else {
    content = fallback;
  }

  return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }
  return context;
}
